import type { AdminBillPaymentSummariesData } from "../api/adminBillPaymentSummaries";
import type { AdminBusinessSummaryData } from "../api/adminBusinessSummary";
import type { AdminBusinessTransaction } from "../api/adminBusinessTransactions";
import type { AdminGiftcardSummariesData } from "../api/adminGiftcardSummaries";
import type { AdminOtcTransactionSummariesData } from "../api/adminOtcTransactionSummaries";
import type { AdminPayoutSummariesData } from "../api/adminPayoutSummaries";
import type { AdminTransactionSummariesData } from "../api/adminTransactionSummaries";
import type {
  AdminBillPaymentTransaction,
  AdminGiftcardTransaction,
  AdminOrderTransaction,
  AdminOtcTransaction,
  AdminPayoutTransaction,
} from "../api/adminTransactionsData";
import { buildBillPaymentTableRows } from "../../pages/bill-payments/billPaymentTransactionsTable";
import { buildMerchantTransactionRows } from "../../pages/merchants/merchantTransactionsTable";
import { buildDeexDeskOtcTableRows } from "../../pages/deex-desk/deexDeskOtcTable";
import { buildGiftcardTableRows } from "../../pages/giftcards/giftcardTransactionsTable";
import { buildOrderTableRows } from "../../pages/orders/ordersOrderTable";
import { buildPayoutTableRows } from "../../pages/payouts/payoutsTransactionsTable";
import type { TransactionExportBundle } from "./fetchTransactionExportBundle";

function csvEscape(cell: string): string {
  const s = String(cell ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function line(cells: string[]): string {
  return cells.map(csvEscape).join(",");
}

function pushKv(rows: string[][], key: string, value: string | number | undefined) {
  rows.push([key, value === undefined || value === null ? "" : String(value)]);
}

function flattenRecordCounts(
  rows: string[][],
  label: string,
  rec: Record<string, number> | undefined
) {
  if (!rec) return;
  for (const [k, v] of Object.entries(rec).sort((a, b) => b[1] - a[1])) {
    rows.push([`${label}.${k}`, String(v)]);
  }
}

function summaryRowsForType(
  type: TransactionExportBundle["meta"]["type"],
  summary: unknown
): string[][] {
  const rows: string[][] = [["Key", "Value"]];

  if (type === "orders") {
    const s = summary as AdminTransactionSummariesData | undefined;
    if (!s) return rows;
    pushKv(rows, "totalOrderCount", s.totalOrderCount);
    pushKv(rows, "totalOrderUSDT", s.totalOrderUSDT);
    pushKv(rows, "totalOrderNGN", s.totalOrderNGN);
    flattenRecordCounts(rows, "statusBreakdown", s.statusBreakdown);
    flattenRecordCounts(rows, "orderTypeBreakdown", s.orderTypeBreakdown);
    if (s.currencyDistribution?.length) {
      rows.push(["", ""]);
      rows.push(["currencyDistribution", ""]);
      for (const c of s.currencyDistribution) {
        rows.push([
          c.currency,
          `pct=${c.percentage};count=${c.count};usdt=${c.usdtAmount};ngn=${c.ngnAmount}`,
        ]);
      }
    }
    return rows;
  }

  if (type === "payouts") {
    const s = summary as AdminPayoutSummariesData | undefined;
    if (!s) return rows;
    pushKv(rows, "totalCount", s.totalCount);
    pushKv(rows, "totalPayoutNGN", s.totalPayoutNGN);
    pushKv(rows, "totalPayoutUSD", s.totalPayoutUSD);
    pushKv(rows, "totalFailed", s.totalFailed);
    pushKv(rows, "totalPending", s.totalPending);
    pushKv(rows, "totalSuccess", s.totalSuccess);
    pushKv(rows, "currency", s.currency);
    flattenRecordCounts(rows, "statusBreakdown", s.statusBreakdown);
    if (s.assetDistribution?.length) {
      rows.push(["", ""]);
      rows.push(["assetDistribution", ""]);
      for (const a of s.assetDistribution) {
        rows.push([
          a.asset,
          `pct=${a.percentage};count=${a.count};usd=${a.payoutAmountUSD};ngn=${a.payoutAmountNGN}`,
        ]);
      }
    }
    return rows;
  }

  if (type === "giftcard") {
    const s = summary as AdminGiftcardSummariesData | undefined;
    if (!s) return rows;
    pushKv(rows, "totalCount", s.totalCount);
    pushKv(rows, "totalAmountUSD", s.totalAmountUSD);
    pushKv(rows, "totalPending", s.totalPending);
    pushKv(rows, "totalCompleted", s.totalCompleted);
    pushKv(rows, "totalFailed", s.totalFailed);
    pushKv(rows, "averageOrderValue", s.averageOrderValue);
    pushKv(rows, "currency", s.currency);
    flattenRecordCounts(rows, "statusBreakdown", s.statusBreakdown);
    return rows;
  }

  if (type === "billpayment") {
    const s = summary as AdminBillPaymentSummariesData | undefined;
    if (!s) return rows;
    pushKv(rows, "totalCount", s.totalCount);
    pushKv(rows, "totalAmountNGN", s.totalAmountNGN);
    pushKv(rows, "totalAmountUSDT", s.totalAmountUSDT);
    pushKv(rows, "currency", s.currency);
    flattenRecordCounts(rows, "statusBreakdown", s.statusBreakdown);
    if (s.subtypeBreakdown) {
      for (const [k, v] of Object.entries(s.subtypeBreakdown)) {
        rows.push([
          `subtype.${k}`,
          `count=${v.count};ngn=${v.amountNGN};usdt=${v.amountUSDT}`,
        ]);
      }
    }
    return rows;
  }

  if (type === "businesses") {
    const s = (summary as AdminBusinessSummaryData | undefined)?.summary;
    if (!s) return rows;
    pushKv(rows, "businesses.total", s.businesses.total);
    pushKv(rows, "businesses.approved", s.businesses.approved);
    pushKv(rows, "businesses.pending", s.businesses.pending);
    pushKv(rows, "businesses.rejected", s.businesses.rejected);
    pushKv(rows, "businesses.verified", s.businesses.verified);
    pushKv(rows, "paymentPages.total", s.paymentPages.total);
    pushKv(rows, "deposits.count", s.deposits.count);
    pushKv(rows, "deposits.totalUSD", s.deposits.totalUSD);
    pushKv(rows, "deposits.totalNGN", s.deposits.totalNGN);
    pushKv(rows, "payouts.count", s.payouts.count);
    pushKv(rows, "payouts.totalNGN", s.payouts.totalNGN);
    pushKv(rows, "payouts.completed.count", s.payouts.completed.count);
    pushKv(rows, "payouts.completed.amountNGN", s.payouts.completed.amountNGN);
    pushKv(rows, "payouts.pending.count", s.payouts.pending.count);
    pushKv(rows, "payouts.pending.amountNGN", s.payouts.pending.amountNGN);
    pushKv(rows, "payouts.failed.count", s.payouts.failed.count);
    pushKv(rows, "payouts.failed.amountNGN", s.payouts.failed.amountNGN);
    pushKv(rows, "payouts.unsettled", s.payouts.unsettled);
    if (s.deposits.byCurrency?.length) {
      rows.push(["", ""]);
      rows.push(["deposits.byCurrency", ""]);
      for (const c of s.deposits.byCurrency) {
        rows.push([
          c.currency,
          `count=${c.count};amount=${c.amount};usd=${c.usdAmount}`,
        ]);
      }
    }
    return rows;
  }

  const s = summary as AdminOtcTransactionSummariesData | undefined;
  if (!s) return rows;
  pushKv(rows, "totalCount", s.totalCount);
  pushKv(rows, "totalAmountUSD", s.totalAmountUSD);
  pushKv(rows, "totalAmountNGN", s.totalAmountNGN);
  pushKv(rows, "currency", s.currency);
  flattenRecordCounts(rows, "statusBreakdown", s.statusBreakdown);
  if (s.subtypeBreakdown) {
    flattenRecordCounts(rows, "subtypeBreakdown", s.subtypeBreakdown);
  }
  if (s.currencyDistribution?.length) {
    rows.push(["", ""]);
    rows.push(["currencyDistribution", ""]);
    for (const c of s.currencyDistribution) {
      rows.push([
        c.currency,
        `pct=${c.percentage};count=${c.count};usd=${c.usdAmount};ngn=${c.ngnAmount}`,
      ]);
    }
  }
  return rows;
}

export function getExportMatrices(bundle: TransactionExportBundle): {
  summary: string[][];
  headers: string[];
  rows: string[][];
} {
  const summary = summaryRowsForType(bundle.meta.type, bundle.summary);
  const { headers, rows } = transactionRows(bundle);
  return { summary, headers, rows };
}

function transactionRows(
  bundle: TransactionExportBundle
): { headers: string[]; rows: string[][] } {
  const t = bundle.meta.type;
  const raw = bundle.transactions;

  if (t === "orders") {
    const txs = raw as unknown as AdminOrderTransaction[];
    const built = buildOrderTableRows(txs);
    const headers = ["Date", "Type", "Status", "Amount / detail", "UserId"];
    const rows = built.map((r) => [
      r.displayDate,
      r.orderTypeLabel,
      r.statusLabel,
      r.summary,
      r.userIdFull,
    ]);
    return { headers, rows };
  }

  if (t === "payouts") {
    const txs = raw as unknown as AdminPayoutTransaction[];
    const emptyBanks = new Map<string, string>();
    const built = buildPayoutTableRows(txs, emptyBanks);
    const headers = [
      "Date",
      "User",
      "Asset",
      "USD",
      "NGN",
      "Bank",
      "Account",
      "Status",
      "Provider",
      "Reference",
    ];
    const rows = built.map((r) => [
      r.displayDate,
      r.userName,
      r.assetLabel,
      r.usdLabel,
      r.ngnLabel,
      r.bankLabel,
      r.accountLine,
      r.statusLabel,
      r.providerLabel,
      r.reference,
    ]);
    return { headers, rows };
  }

  if (t === "giftcard") {
    const txs = raw as unknown as AdminGiftcardTransaction[];
    const built = buildGiftcardTableRows(txs);
    const headers = [
      "Date",
      "User",
      "Card",
      "Type",
      "Country",
      "USD",
      "NGN",
      "Status",
      "Attachments",
    ];
    const rows = built.map((r) => [
      r.displayDate,
      r.userName,
      r.cardName,
      r.typeLabel,
      r.country,
      r.usdLabel,
      r.ngnLabel,
      r.statusLabel,
      r.attachmentHint,
    ]);
    return { headers, rows };
  }

  if (t === "billpayment") {
    const txs = raw as unknown as AdminBillPaymentTransaction[];
    const built = buildBillPaymentTableRows(txs);
    const headers = [
      "Date",
      "User",
      "Email",
      "Subtype",
      "Detail",
      "NGN",
      "USDT",
      "Status",
      "WithdrawalRef",
    ];
    const rows = built.map((r) => [
      r.displayDate,
      r.userName,
      r.userEmail,
      r.subtypeLabel,
      r.detailLine,
      r.ngnLabel,
      r.usdtLabel,
      r.statusLabel,
      r.withdrawalRef,
    ]);
    return { headers, rows };
  }

  if (t === "businesses") {
    const txs = raw as unknown as AdminBusinessTransaction[];
    const isDeposit = bundle.meta.businessTxType !== "payouts";
    const built = buildMerchantTransactionRows(
      txs,
      isDeposit ? "deposits" : "payouts"
    );
    const headers = [
      "Date",
      "Business",
      "Owner",
      "Asset",
      isDeposit ? "USD value" : "Payout (NGN)",
      isDeposit ? "Payment page" : "Recipient",
      "Status",
      "Reference",
    ];
    const rows = built.map((r) => [
      r.displayDate,
      r.businessTitle,
      r.ownerTitle,
      r.assetLabel,
      r.amountLabel,
      r.detailTitle,
      r.statusLabel,
      r.reference,
    ]);
    return { headers, rows };
  }

  const txs = raw as unknown as AdminOtcTransaction[];
  const built = buildDeexDeskOtcTableRows(txs);
  const headers = ["Date", "Type", "Status", "Amount / detail", "User"];
  const rows = built.map((r) => [
    r.displayDate,
    r.typeLabel,
    r.statusLabel,
    r.summary,
    r.userTitle,
  ]);
  return { headers, rows };
}

export function buildAdminTransactionExportCsv(
  bundle: TransactionExportBundle,
  title: string
): string {
  const lines: string[] = [];
  lines.push(line(["Export", title]));
  lines.push(line(["Type", bundle.meta.type]));
  lines.push(
    line(["Date range", `${bundle.meta.startDate} to ${bundle.meta.endDate}`])
  );
  if (bundle.meta.orderSubtype) {
    lines.push(line(["Order subtype", bundle.meta.orderSubtype]));
  }
  if (bundle.meta.billSubtypes) {
    lines.push(line(["Bill subtypes", bundle.meta.billSubtypes]));
  }
  if (bundle.meta.otcSubtypes) {
    lines.push(line(["OTC subtypes", bundle.meta.otcSubtypes]));
  }
  if (bundle.meta.businessTxType) {
    lines.push(line(["Merchant tx type", bundle.meta.businessTxType]));
  }
  if (bundle.meta.businessStatus) {
    lines.push(line(["Status filter", bundle.meta.businessStatus]));
  }
  lines.push("");
  lines.push(line(["SECTION", "SUMMARY"]));
  const { summary: sumRows, headers, rows } = getExportMatrices(bundle);
  for (const r of sumRows) {
    lines.push(line(r));
  }
  lines.push("");
  lines.push(line(["SECTION", "TRANSACTIONS"]));
  lines.push(line(headers));
  for (const r of rows) {
    lines.push(line(r));
  }
  return lines.join("\r\n");
}
